import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  connect() {
    const months = JSON.parse(this.data.get("org_checkdate"));
    const money = JSON.parse(this.data.get("org_endmoney"));
    const rates = JSON.parse(this.data.get("org_rate"));
    const departmentMoney = JSON.parse(this.data.get("dept_endmoney"));
    const departmentNames = JSON.parse(this.data.get("dept_short_names"));
    const departmentOrder = departmentNames.map((name, index) => index)
      .sort((a, b) => Number(departmentNames[a].includes('撤销')) - Number(departmentNames[b].includes('撤销')));
    const departments = departmentOrder.map(index => departmentNames[index]);
    this.departmentCount = departments.length;
    const format = value => value == null || !Number.isFinite(Number(value)) ? '暂无数据' : Number(value).toLocaleString('zh-CN', { maximumFractionDigits: 2 });
    const toolbox = { top: 8, right: 8, feature: { saveAsImage: { pixelRatio: 2 } } };

    this.companyChart = echarts.init(this.element.querySelector('#company-design-cash-flow-chart'));
    this.departmentChart = echarts.init(this.element.querySelector('#subsidiary-design-cash-flow-chart'));
    this.companyChart.setOption({
      legend: { top: 12, data: ['现金流', '折算月份'] },
      tooltip: { trigger: 'axis', valueFormatter: format },
      toolbox,
      grid: { left: 24, right: 24, top: 80, bottom: 24, containLabel: true },
      xAxis: { type: 'category', data: months, axisLabel: { formatter: '{value}月' } },
      yAxis: [
        { type: 'value', name: '现金流（万元）', axisLabel: { formatter: format } },
        { type: 'value', name: '折算月份（月）', splitLine: { show: false } }
      ],
      series: [
        {
          name: '现金流', type: 'line', data: money, symbolSize: 7,
          itemStyle: { color: '#8b80f9' }, lineStyle: { width: 3 },
          label: { show: false },
          tooltip: { valueFormatter: value => `${format(value)} 万元` }
        },
        {
          name: '折算月份', type: 'bar', yAxisIndex: 1,
          data: rates.map(value => value == null || !Number.isFinite(Number(value)) ? null : Math.max(0, Math.min(20, Number(value)))),
          itemStyle: { color: '#7896b3', borderRadius: [4, 4, 0, 0] }, barMaxWidth: 28,
          label: { show: false },
          tooltip: { valueFormatter: value => `${format(value)} 月` }
        }
      ]
    });

    const cells = [];
    departmentOrder.forEach((index, row) => {
      const values = departmentMoney[index];
      months.forEach((month, column) => {
        const value = values[column];
        if (value != null && Number.isFinite(Number(value))) cells.push([column, row, Number(value)]);
      });
    });
    const extent = Math.max(1, ...cells.map(cell => Math.abs(cell[2])));
    this.departmentChart.setOption({
      toolbox,
      tooltip: {
        position: 'top', confine: true, renderMode: 'richText',
        formatter: params => `${departments[params.value[1]]}\n${months[params.value[0]]}月：${format(params.value[2])} 万元`
      },
      grid: { left: 280, right: 64, top: 86, bottom: 24 },
      xAxis: {
        type: 'category', data: months, position: 'top',
        axisLabel: { formatter: '{value}月', interval: 0 },
        splitArea: { show: true }
      },
      yAxis: {
        type: 'category', data: departments, inverse: true,
        axisLabel: { interval: 0, width: 250, overflow: 'break', fontSize: 12 },
        axisTick: { show: false }, axisLine: { show: false }
      },
      visualMap: {
        min: -extent, max: extent, dimension: 2, orient: 'horizontal',
        left: 'center', top: 0, itemWidth: 12, itemHeight: 180,
        text: ['正现金流', '负现金流'],
        formatter: format,
        inRange: { color: ['#d76562', '#f2eee8', '#238c91'] }
      },
      dataZoom: this.rowZoomOptions(12),
      graphic: cells.length ? [] : [{
        type: 'text', left: 'center', top: 'middle',
        style: { text: '暂无现金流数据', fill: '#8993a3', fontSize: 16 }
      }],
      series: [{
        name: '现金流', type: 'heatmap', data: cells,
        itemStyle: { borderWidth: 2, borderColor: '#ffffff' },
        emphasis: { itemStyle: { borderColor: '#48566a', borderWidth: 2 } },
        label: { show: false }
      }]
    });

    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(this.element);
    this.changeDensity();
    this.layout();
  }

  rowZoomOptions(rows, start = 0) {
    const visibleRows = Math.min(rows, this.departmentCount);
    const firstRow = Math.max(0, Math.min(start, this.departmentCount - visibleRows));
    return [{
      type: 'slider', yAxisIndex: 0, right: 8, top: 86, bottom: 24,
      show: this.departmentCount > visibleRows,
      startValue: firstRow, endValue: Math.max(0, firstRow + visibleRows - 1),
      filterMode: 'filter', showDetail: false, width: 20, zoomLock: true,
      minValueSpan: Math.max(0, visibleRows - 1), maxValueSpan: Math.max(0, visibleRows - 1)
    }];
  }

  changeDensity() {
    const rows = Number(this.element.querySelector('#cash-flow-row-density').value);
    const currentStart = this.departmentChart.getOption().dataZoom?.[0]?.startValue || 0;
    this.departmentChart.setOption({
      animation: false,
      dataZoom: this.rowZoomOptions(rows, currentStart),
      yAxis: { axisLabel: { overflow: rows > 12 ? 'truncate' : 'break' } }
    }, { replaceMerge: ['dataZoom'] });
  }

  layout() {
    this.companyChart?.resize();
    this.departmentChart?.setOption({ grid: { left: this.element.clientWidth < 700 ? 180 : 280 }, yAxis: { axisLabel: { width: this.element.clientWidth < 700 ? 155 : 250 } } });
    this.departmentChart?.resize();
  }

  disconnect() {
    this.resizeObserver?.disconnect();
    this.companyChart?.dispose();
    this.departmentChart?.dispose();
  }
}
